import { userApiClient, ApiError } from '../api/client';
import type { User } from '../../types';

export interface RegisterData {
  email: string;
  password: string;
  full_name?: string;
}

export interface SignupData {
  email: string;
  password: string;
  termsAccepted: boolean;
}

export interface SignupResponse {
  success: boolean;
  message: string;
  email: string;
  requiresEmailVerification: boolean;
  organizationId: string | null;
}

export interface SignupConfig {
  enabled: boolean;
  mode: 'invitation_only' | 'whitelist' | 'open';
  allowedDomains: string[];
  message: string | null;
}

/**
 * RegistrationService - Handles user registration and email verification
 *
 * Responsibilities:
 * - User registration (basic and signup with join codes)
 * - Email verification (resend and verify OTP)
 * - Signup configuration retrieval
 */
class RegistrationService {
  /**
   * Register a new user (basic registration)
   *
   * @param data - Registration data
   * @returns The created user
   * @throws {ApiError} If registration fails
   */
  async register(data: RegisterData): Promise<User> {
    try {
      const response = await userApiClient.post<{ user: User }>(
        '/api/v1/auth/register',
        data,
        { skipAuth: true },
      );
      return response.user;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Registration failed', 0, 'UnknownError', error);
    }
  }

  /**
   * Sign up a new user with email + password.
   *
   * Profile (first/last name) and organization join are now collected in the
   * post-verification onboarding wizard, not on this form.
   *
   * @param data - Signup data: email, password, and terms acceptance
   * @returns Signup response with verification status
   * @throws {ApiError} If signup fails
   */
  async signup(data: SignupData): Promise<SignupResponse> {
    try {
      const response = await userApiClient.post<SignupResponse>(
        '/api/v1/auth/signup',
        {
          email: data.email,
          password: data.password,
          termsAccepted: data.termsAccepted,
          termsVersion: '1.0',
        },
        { skipAuth: true },
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Signup failed', 0, 'UnknownError', error);
    }
  }

  /**
   * Resend email verification code
   *
   * @param email - The user's email address
   * @returns Success status and message
   * @throws {ApiError} If resend fails
   */
  async resendVerificationEmail(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await userApiClient.post<{ success: boolean; message: string }>(
        '/api/v1/auth/resend-verification',
        { email },
        { skipAuth: true },
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to resend verification email', 0, 'UnknownError', error);
    }
  }

  /**
   * Verify email using OTP code
   *
   * @param email - The user's email address
   * @param token - The OTP token from email
   * @throws {ApiError} If verification fails
   */
  async verifyOtp(email: string, token: string): Promise<void> {
    try {
      await userApiClient.post(
        '/api/v1/auth/verify-otp',
        {
          email,
          token,
          type: 'signup',
        },
        { skipAuth: true },
      );
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to verify code', 0, 'UnknownError', error);
    }
  }

  /**
   * Get signup configuration settings
   *
   * @returns Signup configuration (mode, allowed domains, etc.)
   * @throws {ApiError} If request fails
   */
  async getSignupConfig(): Promise<SignupConfig> {
    try {
      const response = await userApiClient.get<SignupConfig>('/api/v1/auth/signup/config', {
        skipAuth: true,
      });
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get signup configuration', 0, 'UnknownError', error);
    }
  }
}

// Export singleton instance
export const registrationService = new RegistrationService();
